import { referenciaisDaColuna, type EixoColuna } from '../geometria/anguloExtracao'
import { subtrair, vetor, type Vetor3 } from '../geometria/vetor3'
import {
  RECUO_TRILHO,
  encostoRebateParaFrente,
  centrosDoBanco,
  idsDoBanco,
  larguraDoBanco,
  medidasDaAncora,
} from './bancos'
import { estadoDe, type MapaEstados } from './estadoPecas'
import { nomeDe } from './catalogo'
import { divisoriaPortaMalas, vidroTraseiroEFixo } from './acessoTraseiro'
import { prateleiraTraseira } from './pontosCinto'
import {
  montanteColunaC,
  raioColuna,
  xDaEstrutura,
  yFaceInternaDoTeto,
  zDaColunaNaAltura,
  type AncoraOcupante,
  type IdColuna,
  type PerfilVeiculo,
} from './perfilVeiculo'
import { alturaSentado, type ProporcoesVitima } from './perfisVitima'
import type { RotaExtracao } from './rotasExtracao'

/**
 * Volume que pode atrapalhar a passagem do corpo.
 *
 * Aproximação deliberadamente grosseira: um cilindro vertical com faixa de
 * altura. O aluno entende o critério — "isto está no caminho, nesta altura" — e
 * ele já separa as saídas que servem das que não servem. Detecção de colisão de
 * malha traria precisão que a maquete não tem como honrar.
 */
/**
 * Estrutura exige corte; interior o aluno resolve reclinando, recuando ou
 * removendo. Separar os dois muda a decisão que ele toma diante da lista.
 */
export type TipoObstaculo = 'estrutura' | 'interior'

export interface VolumeObstaculo {
  idPeca: string
  nome: string
  tipo: TipoObstaculo
  /** centro no plano horizontal */
  centro: Vetor3
  /** meia-largura do volume */
  raio: number
  alturaMin: number
  alturaMax: number
  /**
   * Fecha a passagem na horizontal e não pende do teto: fica fora da folga
   * vertical do veículo capotado, onde só contam as peças que o corpo roça por baixo.
   */
  semFolgaVertical?: boolean
}

export interface ObstaculoRota {
  idPeca: string
  nome: string
  tipo: TipoObstaculo
  /** distância horizontal entre o eixo da peça e o eixo da rota */
  distancia: number
  /** folga depois de descontar ombros e o volume; negativa = interfere */
  folga: number
  interfere: boolean
}

/** Distância, no plano XZ, de um ponto ao segmento de reta. */
function distanciaAoSegmento(ponto: Vetor3, de: Vetor3, para: Vetor3): number {
  const d = { x: para.x - de.x, z: para.z - de.z }
  const tamanho = Math.hypot(d.x, d.z)
  if (tamanho < 1e-9) return Math.hypot(ponto.x - de.x, ponto.z - de.z)
  const p = { x: ponto.x - de.x, z: ponto.z - de.z }
  const t = Math.max(0, Math.min(1, (p.x * d.x + p.z * d.z) / (tamanho * tamanho)))
  return Math.hypot(p.x - d.x * t, p.z - d.z * t)
}

const COLUNAS = [
  { chave: 'colunaA', nome: 'Coluna A' },
  { chave: 'colunaB', nome: 'Coluna B' },
  { chave: 'colunaC', nome: 'Coluna C' },
] as const

const LADOS = [
  { sinal: 1, sufixo: 'Esquerda', nome: 'esquerda' },
  { sinal: -1, sufixo: 'Direita', nome: 'direita' },
] as const

/**
 * Colunas estruturais: da soleira ao teto, praticamente toda a altura útil.
 *
 * Coluna cortada sai da conta. É o que a técnica de teto compra, e é o que o
 * aluno vê acontecer ao remover a peça pelo painel.
 */
function volumesDasColunas(perfil: PerfilVeiculo, estados: MapaEstados): VolumeObstaculo[] {
  const saida: VolumeObstaculo[] = []
  for (const lado of LADOS) {
    const x = xDaEstrutura(perfil, lado.sinal)
    for (const coluna of COLUNAS) {
      const idPeca = `${coluna.chave}${lado.sufixo}`
      if (estadoDe(estados, idPeca) === 'removida') continue
      const faixa =
        coluna.chave === 'colunaB'
          ? { zBase: perfil.colunaB.z, zTopo: perfil.colunaB.z }
          : coluna.chave === 'colunaC'
            ? montanteColunaC(perfil)
            : perfil[coluna.chave]
      // usa o z da coluna no meio da faixa de vidros, onde o corpo passa
      const z = zDaColunaNaAltura(perfil, faixa, (perfil.cinturaY + perfil.tetoY) / 2)
      const idColuna: IdColuna =
        coluna.chave === 'colunaA' ? 'A' : coluna.chave === 'colunaB' ? 'B' : 'C'
      saida.push({
        idPeca,
        nome: `${coluna.nome} ${lado.nome}`,
        tipo: 'estrutura',
        centro: vetor(x, 0, z),
        raio: raioColuna(idColuna),
        alturaMin: perfil.topoSoleiraY,
        alturaMax: perfil.tetoY,
      })
    }
  }
  return saida
}

/**
 * Volumes do banco de uma posição, já considerando o que o aluno fez com ele.
 *
 * Reclinar o encosto baixa o topo dele; recuar o assento no trilho o afasta;
 * remover qualquer peça a tira da conta. É esse retorno imediato que mostra ao
 * aluno o quanto cada peça atrapalha.
 */
function volumesDoBanco(
  perfil: PerfilVeiculo,
  ancora: AncoraOcupante,
  estados: MapaEstados,
): VolumeObstaculo[] {
  const m = medidasDaAncora(ancora)
  const ids = idsDoBanco(ancora)
  const raio = larguraDoBanco(perfil, ancora) / 2
  const base = ancora.alturaAssento
  const saida: VolumeObstaculo[] = []

  const reclinado = estadoDe(estados, ids.encosto) === 'aberta'
  const centros = centrosDoBanco(ancora, reclinado)

  const conjuntoRemovido = estadoDe(estados, ids.conjunto) === 'removida'
  const visivel = (id: string) => !conjuntoRemovido && estadoDe(estados, id) !== 'removida'
  // o apoio de cabeça é montado no encosto: se o encosto sai, ele vai junto
  const encostoPresente = visivel(ids.encosto)

  if (visivel(ids.assento)) {
    const recuado = estadoDe(estados, ids.assento) === 'afastada'
    saida.push({
      idPeca: ids.assento,
      nome: nomeDe(ids.assento),
      tipo: 'interior',
      centro: vetor(centros.assento.x, 0, centros.assento.z - (recuado ? RECUO_TRILHO : 0)),
      raio,
      alturaMin: base - m.assento.altura / 2,
      alturaMax: base + m.assento.altura / 2,
    })
  }

  if (encostoPresente) {
    // rebatido para a frente, o encosto de trás fica deitado sobre o assento: a
    // altura que ele ocupa é a da almofada mais a espessura dele, não a de um
    // encosto em pé
    const deitado = reclinado && encostoRebateParaFrente(ancora)
    saida.push({
      idPeca: ids.encosto,
      nome: nomeDe(ids.encosto),
      tipo: 'interior',
      centro: vetor(centros.encosto.x, 0, centros.encosto.z),
      raio: raio - 0.01,
      alturaMin: deitado ? base : Math.min(base, centros.encosto.y - m.encosto.altura / 2),
      alturaMax: deitado
        ? base + m.assento.altura / 2 + m.encosto.espessura
        : centros.encosto.y + m.encosto.altura / 2,
    })
  }

  if (encostoPresente && visivel(ids.apoioCabeca)) {
    saida.push({
      idPeca: ids.apoioCabeca,
      nome: nomeDe(ids.apoioCabeca),
      tipo: 'interior',
      centro: vetor(centros.apoioCabeca.x, 0, centros.apoioCabeca.z),
      raio: m.apoioCabeca.largura / 2,
      alturaMin: centros.apoioCabeca.y - m.apoioCabeca.altura / 2,
      alturaMax: centros.apoioCabeca.y + m.apoioCabeca.altura / 2,
    })
  }

  return saida
}

/** Volante e coluna de direção: obstáculo clássico da vítima do motorista. */
function volumeDoVolante(perfil: PerfilVeiculo, estados: MapaEstados): VolumeObstaculo[] {
  if (estadoDe(estados, 'volante') === 'removida') return []
  const motorista = perfil.ancoras.find((a) => a.id === 'motorista')
  if (!motorista) return []
  const y = perfil.cinturaY - 0.14
  return [
    {
      idPeca: 'volante',
      nome: nomeDe('volante'),
      tipo: 'interior',
      centro: vetor(motorista.posicao.x, 0, perfil.colunaA.zBase - 0.36),
      raio: 0.18,
      alturaMin: y - 0.18,
      alturaMax: y + 0.18,
    },
  ]
}

/**
 * O que fecha a cabine por trás no sedan: o vidro traseiro, fixo, e a prateleira
 * sob ele. Quem sai pela traseira passa por ali, e é isso que torna a saída do
 * sedan mais difícil que a do hatch — onde o vidro sobe junto com a tampa e não
 * há volume nenhum aqui.
 */
function volumesDoFechamentoTraseiro(perfil: PerfilVeiculo, estados: MapaEstados): VolumeObstaculo[] {
  if (!vidroTraseiroEFixo(perfil)) return []
  const saida: VolumeObstaculo[] = []
  if (estadoDe(estados, 'vidroTraseiro') !== 'removida') {
    saida.push({
      idPeca: 'vidroTraseiro',
      nome: nomeDe('vidroTraseiro'),
      tipo: 'interior',
      centro: vetor(0, 0, (perfil.colunaC.zBase + perfil.colunaC.zTopo) / 2),
      raio: perfil.largura * 0.34,
      alturaMin: perfil.cinturaY,
      alturaMax: perfil.tetoY,
      semFolgaVertical: true,
    })
  }
  // a divisória não sai com o vidro nem com o banco: só com corte. É o único
  // obstáculo 'estrutura' da traseira, e por isso a retirada a 0° no sedan
  // continua marcada em vermelho mesmo com o resto preparado
  const divisoria = divisoriaPortaMalas(perfil)
  if (divisoria && estadoDe(estados, 'divisoriaPortaMalas') !== 'removida') {
    saida.push({
      idPeca: 'divisoriaPortaMalas',
      nome: nomeDe('divisoriaPortaMalas'),
      tipo: 'estrutura',
      centro: vetor(0, 0, divisoria.z),
      raio: divisoria.meiaLargura,
      alturaMin: divisoria.yBase,
      alturaMax: divisoria.yTopo,
      semFolgaVertical: true,
    })
  }
  const prateleira = prateleiraTraseira(perfil)
  if (prateleira && estadoDe(estados, 'prateleiraTraseira') !== 'removida') {
    saida.push({
      idPeca: 'prateleiraTraseira',
      nome: nomeDe('prateleiraTraseira'),
      tipo: 'interior',
      centro: vetor(0, 0, (prateleira.zFrente + prateleira.zTras) / 2),
      raio: perfil.largura * 0.3,
      alturaMin: prateleira.yTopo - 0.05,
      alturaMax: prateleira.yTopo,
      semFolgaVertical: true,
    })
  }
  return saida
}

/** Todos os volumes que podem atrapalhar, no estado atual da maquete. */
export function volumesObstaculo(
  perfil: PerfilVeiculo,
  estados: MapaEstados = {},
): VolumeObstaculo[] {
  return [
    ...volumesDasColunas(perfil, estados),
    ...perfil.ancoras.flatMap((a) => volumesDoBanco(perfil, a, estados)),
    ...volumeDoVolante(perfil, estados),
    ...volumesDoFechamentoTraseiro(perfil, estados),
  ]
}

/**
 * Faixa de altura ocupada pelo corpo, da pelve ao topo da cabeça.
 *
 * Projetada no eixo REAL do corpo, não em +Y: com a vítima deitada no teto do
 * veículo capotado, somar a altura sentada em vertical devolvia uma faixa que
 * subia 90 cm acima do teto — e aí nenhum banco cruzava a faixa, restando só as
 * colunas na conta. Sentada o eixo é +Y e o resultado é idêntico ao anterior.
 */
function faixaDoCorpo(eixoColuna: EixoColuna, proporcoes: ProporcoesVitima): [number, number] {
  const { eixo } = referenciaisDaColuna(eixoColuna)
  const yPelve = eixoColuna.baseColuna.y - eixo.y * (proporcoes.alturaPelve / 2)
  const yCabeca = yPelve + eixo.y * alturaSentado(proporcoes)
  return [Math.min(yPelve, yCabeca), Math.max(yPelve, yCabeca)]
}

const cruzam = (a: [number, number], b: [number, number]): boolean => a[0] <= b[1] && b[0] <= a[1]

/**
 * Obstáculos que o corpo encontra ao percorrer a rota.
 *
 * Só entra na conta o volume cuja faixa de altura cruza a do corpo: é o que faz
 * o apoio de cabeça importar e o assento, que fica abaixo, não.
 */
export function obstaculosDaRota(
  perfil: PerfilVeiculo,
  eixoColuna: EixoColuna,
  rota: RotaExtracao,
  proporcoes: ProporcoesVitima,
  estados: MapaEstados = {},
): ObstaculoRota[] {
  const corpo = faixaDoCorpo(eixoColuna, proporcoes)
  const raioCorpo = proporcoes.larguraOmbros / 2

  return volumesObstaculo(perfil, estados)
    .filter((v) => cruzam(corpo, [v.alturaMin, v.alturaMax]))
    .map((v) => {
      const distancia = distanciaAoSegmento(v.centro, eixoColuna.baseColuna, rota.pontoSaida)
      const folga = distancia - (raioCorpo + v.raio)
      return { idPeca: v.idPeca, nome: v.nome, tipo: v.tipo, distancia, folga, interfere: folga < 0 }
    })
    .sort((a, b) => a.folga - b.folga)
}

/** Só o que atrapalha, já na ordem do pior para o melhor. */
export function interferencias(
  perfil: PerfilVeiculo,
  eixoColuna: EixoColuna,
  rota: RotaExtracao,
  proporcoes: ProporcoesVitima,
  estados: MapaEstados = {},
): ObstaculoRota[] {
  return obstaculosDaRota(perfil, eixoColuna, rota, proporcoes, estados).filter((o) => o.interfere)
}

/** Folga mínima da rota: quanto menor, mais apertada a passagem. */
export function folgaMinima(
  perfil: PerfilVeiculo,
  eixoColuna: EixoColuna,
  rota: RotaExtracao,
  proporcoes: ProporcoesVitima,
  estados: MapaEstados = {},
): number {
  const todos = obstaculosDaRota(perfil, eixoColuna, rota, proporcoes, estados)
  return todos.length === 0 ? Number.POSITIVE_INFINITY : todos[0]!.folga
}

/** Comprimento horizontal do percurso, útil para comparar saídas. */
export function comprimentoDaRota(eixoColuna: EixoColuna, rota: RotaExtracao): number {
  const d = subtrair(rota.pontoSaida, eixoColuna.baseColuna)
  return Math.hypot(d.x, d.z)
}

export interface FolgaVertical {
  /** vão livre entre a face interna do teto e a peça mais baixa sobre a rota */
  vao: number
  /** vão descontada a espessura do corpo deitado; negativa = não passa */
  folga: number
  idPeca: string | null
  nome: string
}

/**
 * Folga VERTICAL da rota, que é a que decide no veículo capotado.
 *
 * Deitada no teto, a vítima não disputa espaço lateral com os bancos: eles ficam
 * acima dela, pendurados. O que a impede de passar é o vão entre o teto — agora
 * o piso — e a peça mais baixa no caminho, que é tipicamente o apoio de cabeça
 * dianteiro. Só entram as peças cujo rastro no plano XZ o corpo atravessa: um
 * apoio de cabeça traseiro não limita uma retirada que passa longe dele.
 *
 * Reclinar o encosto, recuar o banco ou remover o apoio de cabeça mudam o número
 * na hora, porque os volumes já vêm do estado das peças.
 */
export function folgaVerticalNoTeto(
  perfil: PerfilVeiculo,
  eixoColuna: EixoColuna,
  rota: RotaExtracao,
  proporcoes: ProporcoesVitima,
  estados: MapaEstados = {},
): FolgaVertical {
  const teto = yFaceInternaDoTeto(perfil)
  const espessuraCorpo = proporcoes.profundidadeTronco
  const raioCorpo = proporcoes.larguraOmbros / 2

  let vao = teto - perfil.pisoY
  let idPeca: string | null = null
  let nome = 'nenhuma peça sobre a rota'

  for (const v of volumesObstaculo(perfil, estados)) {
    if (v.tipo !== 'interior' || v.semFolgaVertical) continue
    // fora do rastro do corpo a peça não limita a passagem, por mais baixa que seja
    const distancia = distanciaAoSegmento(v.centro, eixoColuna.baseColuna, rota.pontoSaida)
    if (distancia > raioCorpo + v.raio) continue
    const candidato = teto - v.alturaMax
    if (candidato < vao) {
      vao = candidato
      idPeca = v.idPeca
      nome = v.nome
    }
  }

  return { vao, folga: vao - espessuraCorpo, idPeca, nome }
}
